import { Component, OnInit, OnDestroy, HostListener, ViewChild, TemplateRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, merge, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger, UntilDestroy, untilDestroyed } from '@shared';
import { I18nService } from '@app/i18n';
import { AuthenticationService } from './auth';
import { ConfirmationService } from './auth/confirmation.service';
import { ErrorService } from './pages/error/error.service';
import { HttploadingSpinnerComponent } from './@shared/httploading-spinner/httploading-spinner.component';
import { PREFETCH_MARKER_PATTERN, PrefetchService } from './@shared/services/prefetch.service';
import { TabManagerService } from './@shared/services/tab-manager.service';
import { ServiceWorkerUpdateService } from './@shared/services/service-worker-update.service';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
// import { DemoAuthService } from './demo-auth/demo-auth.service';
declare const mixpanel: any;
const log = new Logger('App');
declare const window: any;
@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private channel: BroadcastChannel;
  @ViewChild('content', { static: true }) contentRef!: TemplateRef<any>;
  message = 'Application is already opened in another tab.';
  notificationMessage = this.message;
  showDemoLogin$: Observable<boolean> = of(false);
  public filteredUrls: string[] = [];
  public httploadingSpinnerComponent = HttploadingSpinnerComponent;

  /** Whether the "new version available" banner is visible. */
  showUpdateBanner = false;
  /** Seconds remaining before the page reloads onto the new version. */
  updateCountdown = 10;
  private updateCountdownTimer: any;
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private authService: AuthenticationService,
    private confirmationService: ConfirmationService,
    private errorService: ErrorService,
    private tabManagerService: TabManagerService,
    private serviceWorkerUpdateService: ServiceWorkerUpdateService,
    private config: NgbModalConfig,
    private modalService: NgbModal,
    // private demoAuthService: DemoAuthService,
    private prefetchService: PrefetchService
  ) {
    this.config.backdrop = 'static';
    this.config.keyboard = false;
    this.channel = new BroadcastChannel('app_channel');
    // this.showDemoLogin$ = combineLatest([
    //   this.demoAuthService.checkAuthStatus(),
    //   this.demoAuthService.isAuthenticated$
    // ]).pipe(
    //   map(([status, isAuthenticated]) =>
    //     status.demoAuthEnabled && !isAuthenticated
    //   )
    // );
    
  }

  ngOnInit() {
    this.serviceWorkerUpdateService.init();
    this.serviceWorkerUpdateService.versionReady$.pipe(untilDestroyed(this)).subscribe(() => {
      this.startUpdateCountdown();
    });
    this.tabManagerService.detectNewTabs();
    this.tabManagerService.tabChangeEvent.subscribe((data) => {
      const pathName = window.location.pathname;
      if (!['/'].includes(pathName) && data) {
        if(data?.sessionExpired) {
          this.notificationMessage = data?.message;
        }else {
          this.notificationMessage = this.message;
        }
        this.modalService.open(this.contentRef, { centered: false }).result.then((result) => {
          this.modalService.dismissAll();
          if(data?.sessionExpired) {
            this.notificationMessage = data?.message;
          }else {
            if (result === 'ok' && data.logout) {
              this.authService.logOut(data.nextUrl.split('/'));
            } else if (result === 'ok' && !data.logout) {
              this.router.navigate([data.nextUrl]);
            }
          }
        });
      }
    });

    // capture navigation end events
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe((event: any) => {
        window.scrollTo(0, 0);
        // Drop the prefetch cache when a fresh flow starts (landing/login) or completes (success).
        const url = (event.urlAfterRedirects || '').split('?')[0];
        if (['/', '/login', '/success'].includes(url)) {
          this.prefetchService.clearCache();
        }
        if (!['/'].includes(event.urlAfterRedirects)) {
          this.tabManagerService.postMessage();
        }
      });

    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(arguments);
    }
    gtag('js', new Date());
    gtag('config', environment.gaKey, { page_location: this.getSanitizedUrl() });

    this.filteredUrls = ['orders/orderInfoZohoStatus', 'agreement/status/', PREFETCH_MARKER_PATTERN]; // spinner should be hidden/filtered for these urls
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this),
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    mixpanel.init(environment.mixPanel, {
      autocapture: false,
      debug: !environment.production,
      track_pageview: false,
      persistence: 'localStorage',
    });
  }

  /** Current URL without the `token` query parameter, which carries a credential. */
  private getSanitizedUrl(): string {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    return url.toString();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: any): void {
    event.preventDefault(); // Cancel the event to prevent the browser prompt
    this.confirmationService.confirm('Are you sure you want to refresh this page?').subscribe((confirmed: boolean) => {
      if (confirmed) {
        event.returnValue = true; // Proceed with refresh if confirmed
      }
    });
  }

  /** Shows the update banner and reloads the page once the countdown reaches zero. */
  private startUpdateCountdown(): void {
    if (this.showUpdateBanner) {
      return; // countdown already running
    }
    this.showUpdateBanner = true;
    this.updateCountdown = 10;
    this.updateCountdownTimer = setInterval(() => {
      this.updateCountdown -= 1;
      if (this.updateCountdown <= 0) {
        this.reloadNow();
      }
    }, 1000);
  }

  /** Immediately reloads the page onto the new version. */
  reloadNow(): void {
    if (this.updateCountdownTimer) {
      clearInterval(this.updateCountdownTimer);
      this.updateCountdownTimer = null;
    }
    this.serviceWorkerUpdateService.reloadApp();
  }

  ngOnDestroy() {
    if (this.updateCountdownTimer) {
      clearInterval(this.updateCountdownTimer);
    }
    this.i18nService.destroy();
    this.channel.close();
    this.authService.logOut();
    this.errorService.clearError();
  }
}
